import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserCircle, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pickNudge, readDismissals, dismiss } from "@/lib/profileNudge";

// One dismissible, contextual invitation to fill a relevant profile section. Never a gate; never writes.
export function ProfileNudge({ surface, priorities, lead, when = true, className = "" }) {
  const { user } = useAuth();
  const [nudge, setNudge] = useState(null);

  useEffect(() => {
    if (!user || !when) { setNudge(null); return; }
    let live = true;
    api.get("/profile/completeness")
      .then(({ data }) => { if (live) setNudge(pickNudge(data, priorities, surface, readDismissals())); })
      .catch(() => { if (live) setNudge(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, when, surface]);

  if (!nudge) return null;
  return (
    <aside data-testid={`profile-nudge-${surface}`} aria-label="Suggestion for your profile"
      className={`relative rounded-xl border border-[#4C1D95]/20 bg-[#F5F0FF] p-4 pr-12 ${className}`}>
      <div className="flex items-start gap-3">
        <UserCircle className="h-5 w-5 mt-0.5 shrink-0 text-[#4C1D95]" aria-hidden="true" />
        <div className="min-w-0 text-sm text-stone-700 leading-relaxed">
          <p>{lead} {lead.trim().endsWith(",") ? "it" : "It"} would help to know <strong>{nudge.hint}</strong>. It's optional, and only what you enter is saved.</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <Link to={`/profile#profile-section-${nudge.key}`} data-testid={`profile-nudge-${surface}-open`}
              className="font-semibold text-[#4C1D95] underline underline-offset-2">
              Add {nudge.title.toLowerCase()}
            </Link>
            <button type="button" data-testid={`profile-nudge-${surface}-later`} onClick={() => { dismiss(surface, nudge.key); setNudge(null); }}
              className="text-stone-500 underline underline-offset-2">
              Not now
            </button>
          </div>
        </div>
      </div>
      <button type="button" aria-label="Dismiss suggestion" data-testid={`profile-nudge-${surface}-close`}
        onClick={() => { dismiss(surface, nudge.key); setNudge(null); }}
        className="absolute right-2 top-2 inline-flex h-9 w-9 items-center justify-center rounded-md text-stone-500 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}
